<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { hideTonPayment, useInit } from '@/composables/init'
import TheHeader from '@/components/TheHeader.vue'
import { computed, type ComputedRef, nextTick, onBeforeMount, onMounted, provide, ref, watch } from 'vue'
import TheNavbar from '@/components/TheNavbar.vue'
import { usePlayer } from '@/composables/game/player'
import FlyingCoins from '@/components/FlyingCoins.vue'
import { useAnimateCoins, useFlyingCoins } from '@/composables/game/flying-coins'
import OfflineProfitModal from '@/components/modals/OfflineProfitModal.vue'
import Toaster from '@/components/TheToaster.vue'
import AllDailyDoneOverlay from '@/components/overlays/AllDailyDoneOverlay.vue'
import { useAllDailyCheck, useDailyRadarTx, useDailyTx } from '@/composables/game/daily'
import LevelUpModal from '@/components/modals/LevelUpModal.vue'
import { useConfig } from '@/composables/game/config'
import TokenChangedModal from '@/components/modals/TokenChangedModal.vue'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import ExtraSpinOverlay from '@/components/overlays/ExtraSpinOverlay.vue'
import { useLocalStorage, watchOnce } from '@vueuse/core'
import { useTranslate } from '@tolgee/vue'
import { useTaskHighlight } from '@/composables/game/task-highlight'
import TaskHighlightModal from '@/components/modals/TaskHighlightModal.vue'
import { showOnboardedFromS1Modal, useOnboarding } from '@/composables/game/onboarding'
import { useGuide } from '@/composables/game/guide'
import StarBalanceConfirmModal from '@/components/modals/StarBalanceConfirmModal.vue'
import {
  type DailyUpWinnerNotification,
  isBadgeNotification,
  isDailyUpWinnerNotification,
  isLiveOpsChallengeWinnerNotification,
  isMemoryGameChallengeWinnerNotification,
  isMinesweeperGameChallengeWinnerNotification,
  isPathwayChallengeWinnerNotification,
  isPuzzleChallengeWinnerNotification,
  isPuzzlesCompletionChallengeWinnerNotification,
  isSpecialCollectibleChallengeWinnerNotification,
  type LiveOpsChallengeWinnerNotification, type MemoryGameChallengeWinnerNotification,
  type MinesweeperGameChallengeWinnerNotification,
  type PathwayChallengeWinnerNotification,
  type PuzzleChallengeWinnerNotification,
  type PuzzlesCompletionChallengeWinnerNotification,
  type SpecialCollectibleWinnerNotification,
} from '@/types/shared/player'
import { useConfetti } from '@/composables/game/confetti'
import { allBadgesMap, useBadges } from '@/composables/game/badge'
import ChallengeModal from '@/components/modals/CardChallengeModal.vue'
import { useCardChallengeLeaderboard } from '@/composables/game/leader-board'
import NumberFormattingModal from '@/components/modals/NumberFormattingModal.vue'
import { useModals } from '@/composables/modal-helper'
import BonusOverlay from '@/components/overlays/BonusOverlay.vue'
import AchievementOverlay from '@/components/overlays/AchievementOverlay.vue'
import { useAchievementsPage, useAchievementsTracker } from '@/composables/game/achievements'
import useIsScreenSmallerThan from '@/composables/helpers/is-screen-smaller-than'
import PathwayEventOverlay from '@/components/overlays/PathwayEventOverlay.vue'
import { usePathwayEventOverlay } from '@/composables/game/pathway-events'
import PuzzlesOverlay from '@/components/overlays/PuzzlesOverlay.vue'
import { usePuzzlesOverlay } from '@/composables/game/puzzles'
import DailyUpChallengeModal from '@/components/modals/DailyUpChallengeModal.vue'
import BadgeOverlay from '@/components/overlays/BadgeOverlay.vue'
import DailyUpChallengeWinnerModal from '@/components/modals/DailyUpChallengeWinnerModal.vue'
import PathwayChallengeModal from '@/components/modals/PathwayChallengeModal.vue'
import PathwayChallengeWinnerModal from '@/components/modals/PathwayChallengeWinnerModal.vue'
import MemoryChallengeOverlay from '@/components/overlays/MemoryChallengeOverlay.vue'
import MemoryChallengeWinnerModal from '@/components/modals/MemoryChallengeWinnerModal.vue'
import { useMemoryChallengeModal } from '@/composables/game/memory-challenge-modal'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { useKeyboardOpen } from '@/composables/helpers/keyboard'
import PuzzleChallengeModal from '@/components/modals/PuzzleChallengeModal.vue'
import PuzzleChallengeWinnerModal from '@/components/modals/PuzzleChallengeWinnerModal.vue'
import { useRouterRemoveQueryParam } from '@/composables/game/router'
import { ECOMMERCE_ITEM_PLACEMENT, MINI_GAME_ID } from '@/types/shared/player-config'
import MinesweeperChallengeOverlay from '@/components/overlays/MinesweeperChallengeOverlay.vue'
import { useMinesweeperChallengeModal } from '@/composables/game/minesweeper-challenge-modal'
import { useGiftModal } from '@/composables/game/gift-modal'
import ViewGiftModal from '@/components/modals/ViewGiftModal.vue'
import MinesweeperChallengeWinnerModal from '@/components/modals/MinesweeperChallengeWinnerModal.vue'
import CollectibleChallengeOverlay from '@/components/overlays/CollectibleChallengeOverlay.vue'
import { useCollectibleChallengeModal } from '@/composables/game/collectible-challenge-modal'
import LiveOpsChallengeOverlay from '@/components/overlays/LiveOpsChallengeOverlay.vue'
import { useLiveOpsChallengeModal } from '@/composables/game/live-ops-challenge-modal'
import CollectibleChallengeWinnerModal from '@/components/modals/CollectibleChallengeWinnerModal.vue'
import LiveOpsChallengeWinnerModal from '@/components/modals/LiveOpsChallengeWinnerModal.vue'
import { useV1Stars } from '@/composables/game/v1-stars'
import PuzzlesCompletionChallengeModal from '@/components/modals/PuzzlesCompletionChallengeModal.vue'
import PuzzlesCompletionChallengeWinnerModal from '@/components/modals/PuzzlesCompletionChallengeWinnerModal.vue'
import ExchangeOverlay from '@/components/overlays/ExchangeOverlay.vue'
import { useExchangeOverlay } from '@/composables/game/exchange'
import BaseBackButton from '@/components/BaseBackButton.vue'
import LimitedPackModal from '@/components/modals/LimitedPackModal.vue'
import { type FrontendLimitedPack, usePacks } from '@/composables/game/shop'
import { useMysteryBoxes } from '@/composables/game/mystery-boxes'
import { fetchLeaderboards } from '@/composables/game/live-ops-challenge'
import { initialLiveOpsEvents } from '@/composables/game/live-ops-events'
import { useLiveOpsOverlay } from '@/composables/game/live-ops-overlay'
import LiveOpsOverlay from '@/components/overlays/LiveOpsOverlay.vue'
import { useMultiplayerMine } from '@/composables/game/multiplayer-mine'
import PlantMineModal from '@/components/modals/PlantMineModal.vue'
import MineTutorialModal from '@/components/modals/MineTutorialModal.vue'
import UseMagnetOverlay from '@/components/overlays/UseMagnetOverlay.vue'
import MinesCollectedModal from '@/components/modals/MinesCollectedModal.vue'

type ExtraSpinDetails = {
	count: number,
	description: string
}

const WebApp = Telegram.WebApp
const route = useRoute()
const meta = computed(() => route.meta)
const hideHeader = computed((): boolean => Boolean(meta.value.hideHeader) ?? false)
const hideNavbar = computed((): boolean => Boolean(meta.value.hideNavbar) ?? false)
const { coins, offlineProfit, player, chosenToken, offlineProfitHours, notifications, notificationReward } = usePlayer()
const { config } = useConfig()
const { showFlyingCoins, startCoinAnimation } = useFlyingCoins()
const { allDailyDone } = useAllDailyCheck()
const { doAnimateCoins } = useAnimateCoins()
const { t } = useTranslate()
const { isOnboardingDone } = useOnboarding()
const { initGuide } = useGuide()
const { showConfetti } = useConfetti()
const { getBadges } = useBadges()
const { setCardChallengeModalTimestamp } = useCardChallengeLeaderboard()
const { claimedDailyTx, hasDoneTxToday, setDailyTxModalTimestamp, canShowDailyTxModal: canShowTx } = useDailyTx()
const {
  claimedDailyRadar,
  hasDoneRadarTxToday,
  setDailyRadarTxModalTimestamp,
  canShowDailyRadarTxModal: canShowRadarTx,
} = useDailyRadarTx()
const {
  showNumberFormatModal,
  setShowNumberFormatModal,
  showTransactionModal,
  setShowTransactionModal,
  showRadarTransactionModal,
  setShowRadarTransactionModal,
  showDailyUpChallengeModal,
  setShowDailyUpChallengeModal,
  showPathwayChallengeModal,
  setShowPathwayChallengeModal,
  showPuzzleChallengeModal,
  showPuzzlesCompletionChallengeModal,
  setShowPuzzlesCompletionChallengeModal,
  setShowPuzzleChallengeModal,
  showLimitedPackModal,
  setShowLimitedPackModal,
  selectedLimitedPack,
} = useModals()
const { activeLimitedPackItems } = usePacks(ECOMMERCE_ITEM_PLACEMENT.HOME)
const { shouldRedirectToMysteryBoxSale, markMysteryBoxSaleAsShown } = useMysteryBoxes()
const { selectedGift, showGiftModal, hideGiftModal } = useGiftModal()
const {
  showClaimV1StarsModal,
  fetchV1Stars,
  openClaimV1StarsModal,
} = useV1Stars()
useAchievementsPage()
const {
  showNewAchievedMilestoneOverlay,
  newAchievedMilestoneForOverlay,
  checkForNewAchievementsOnInit,
} = useAchievementsTracker()
const {
  showPathwayEventOverlay,
  selectedPathwayEvent,
  hidePathwayEventOverlay,
} = usePathwayEventOverlay()
const {
  showPuzzlesOverlay,
  hidePuzzlesOverlay,
} = usePuzzlesOverlay()
const {
  openMemoryChallengeModal,
  showMemoryChallengeModal,
  closeMemoryChallengeModal,
} = useMemoryChallengeModal()
const {
  openMinesweeperChallengeModal,
  showMinesweeperChallengeModal,
  closeMinesweeperChallengeModal,
} = useMinesweeperChallengeModal()
const {
  showCollectibleChallengeModal,
  closeCollectibleChallengeModal,
} = useCollectibleChallengeModal()
const {
  showLiveOpsChallengeModal,
  closeLiveOpsChallengeModal,
} = useLiveOpsChallengeModal()
const {
  showExchangeOverlay,
  hideExchangeOverlay,
  selectedExchange,
} = useExchangeOverlay()
const {
  showLiveOpsOverlay,
  selectedLiveOpsEventId,
  openLiveOpsOverlay,
  closeLiveOpsOverlay,
} = useLiveOpsOverlay()
const {
  showPlantMineModal,
  closePlantMineModal,
  showMineTutorialModal,
  closeMineTutorialModal,
  showUseMagnetOverlay,
  closeUseMagnetOverlay,
  showMinesCollectedModal,
  collectedMinesCount,
  closeMinesCollectedModal,
} = useMultiplayerMine()
const { updateState } = useFortuneWheel()
const { newHighlightedTasks, setNewTasksAsClosed } = useTaskHighlight()
const { bottomNumber } = useFullScreenDimensions()
const { isKeyboardOpen } = useKeyboardOpen()
const { removeQueryParams } = useRouterRemoveQueryParam()

const canShowDailyChallengeModal = computed(() => false) //hide the modal from the flow for now
const canShowBonusOverlay = ref(true)
const canShowOfflineProfitModal = ref(false)
const canShowDailyTxModal = computed(() => canShowTx.value && isOnboardingDone.value && !claimedDailyTx.value && !hasDoneTxToday.value && isInitLoad.value && !hideTonPayment.value)
const canShowRadarTxModal = computed(() => canShowRadarTx.value && isOnboardingDone.value && !claimedDailyRadar.value && !hasDoneRadarTxToday.value && isInitLoad.value && !hideTonPayment.value)

const hideAllInitModals = computed(() => WebApp.initDataUnsafe.user?.username === 'Fom98')

const dailyUpWinnerNotification = ref<null | DailyUpWinnerNotification>(null)
const pathwayChallengeWinnerNotification = ref<null | PathwayChallengeWinnerNotification>(null)
const puzzleChallengeWinnerNotification = ref<null | PuzzleChallengeWinnerNotification>(null)
const puzzlesCompletionChallengeWinnerNotification = ref<null | PuzzlesCompletionChallengeWinnerNotification>(null)
const memoryGameWinnerNotification = ref<null | MemoryGameChallengeWinnerNotification>(null)
const minesweeperGameWinnerNotification = ref<null | MinesweeperGameChallengeWinnerNotification>(null)
const specialCollectibleWinnerNotification = ref<null | SpecialCollectibleWinnerNotification>(null)
const liveOpsChallengeWinnerNotification = ref<null | LiveOpsChallengeWinnerNotification>(null)

const isSmallScreen = useIsScreenSmallerThan(375)
const isInitLoad = ref(true)
const coinsWithoutOfflineProfit = computed(() => coins.value - offlineProfit.value)
const addOfflineProfitToCoins = ref(false)
const coinsToShow = computed(() => addOfflineProfitToCoins.value ? coins.value : coinsWithoutOfflineProfit.value)
provide<ComputedRef<number>>('coinsToShow', coinsToShow)

const hasNewHighlightedTasks = computed(() => newHighlightedTasks.value.length > 0)
const hasBonusWithTphX2 = ref(false)
const pendingRedirectCheck = ref(false)

const limitedPackPopupQueue = ref<FrontendLimitedPack[]>([])
const showBonusOverlay = ref(false)
const showOfflineProfitModal = ref(false)
const showLevelUpModal = ref(false)
const showAllDailyDoneOverlay = ref(false)
const showTokenChangedModal = ref(false)
const showUpgradesExtraSpinOverlay = ref(false)
const showHighlightedTasksModal = ref(false)
const showBadgeOverlay = ref(false)
const showChallengeModal = ref(false)

const currentBadgeId = ref<string | undefined>()
const badgeConfig = computed(() => currentBadgeId.value ? allBadgesMap.value[currentBadgeId.value] : undefined)
const currentNotificationIndex = ref<number | undefined>()
const badgeNotifications = computed(() => notifications.value.filter(isBadgeNotification))
const dailyUpWinnerNotifications = computed(() => notifications.value.filter(isDailyUpWinnerNotification))
const pathwayChallengeWinnerNotifications = computed(() => notifications.value.filter(isPathwayChallengeWinnerNotification))
const memoryGameWinnerNotifications = computed(() => notifications.value.filter(isMemoryGameChallengeWinnerNotification))
const minesweeperGameWinnerNotifications = computed(() => notifications.value.filter(isMinesweeperGameChallengeWinnerNotification))
const puzzleChallengeWinnerNotifications = computed(() => notifications.value.filter(isPuzzleChallengeWinnerNotification))
const puzzlesCompletionChallengeWinnerNotifications = computed(() => notifications.value.filter(isPuzzlesCompletionChallengeWinnerNotification))
const specialCollectibleWinnerNotifications = computed(() => notifications.value.filter(isSpecialCollectibleChallengeWinnerNotification))
const liveOpsChallengeWinnerNotifications = computed(() => notifications.value.filter(isLiveOpsChallengeWinnerNotification))

const playerLevel = computed(() => player.value.level)
const playerTapConfig = computed(() => config.value.tap)

const upgradesExtraSpinDetails = computed<ExtraSpinDetails>(() => {
  return {
    count: config.value.game.fortuneWheel.extraSpin.upgrades.amount,
    description: t.value('fortune_wheel.extra_spin.upgrades.description', 'You successfully upgraded 10 cards. Head to Rewards and spin the wheel to earn more!'),
  }
})
const upgradesExtraSpinOverlayDisplayed = useLocalStorage<boolean>('upgradesExtraSpinOverlayDisplayed', false)
const needsFullHeight = computed(() => Boolean(router.currentRoute.value.meta.fullHeight))

const fullScreenStyles = computed(() => {
  return { paddingBottom: needsFullHeight.value ? undefined : `${bottomNumber.value + (hideNavbar.value ? -20 : isSmallScreen.value ? 60 : 70)}px` }
})

function checkForChallengeModal() {
  if (canShowDailyChallengeModal.value) {
    setTimeout(() => {
      setCardChallengeModalTimestamp()
      showChallengeModal.value = true
    }, 600)
  } else {
    checkForDailyUpWinner()
  }
}

function checkForDailyUpWinner() {
  if (dailyUpWinnerNotifications.value.length) {
    dailyUpWinnerNotification.value = dailyUpWinnerNotifications.value[0]
  } else {
    checkForPathwayChallengeWinner()
  }
}

function checkForPathwayChallengeWinner() {
  if (pathwayChallengeWinnerNotifications.value.length) {
    pathwayChallengeWinnerNotification.value = pathwayChallengeWinnerNotifications.value[0]
  } else {
    checkForPuzzlesCompletionChallengeWinner()
  }
}

function checkForPuzzlesCompletionChallengeWinner() {
  if (puzzlesCompletionChallengeWinnerNotifications.value.length) {
    puzzlesCompletionChallengeWinnerNotification.value = puzzlesCompletionChallengeWinnerNotifications.value[0]
  } else {
    checkForPuzzleChallengeWinner()
  }
}

function checkForPuzzleChallengeWinner() {
  if (puzzleChallengeWinnerNotifications.value.length) {
    puzzleChallengeWinnerNotification.value = puzzleChallengeWinnerNotifications.value[0]
  } else {
    checkForMemoryGameWinner()
  }
}

function checkForMemoryGameWinner() {
  if (memoryGameWinnerNotifications.value.length) {
    memoryGameWinnerNotification.value = memoryGameWinnerNotifications.value[0]
  } else {
    checkForMinesweeperGameWinner()
  }
}

function checkForMinesweeperGameWinner() {
  if (minesweeperGameWinnerNotifications.value.length) {
    minesweeperGameWinnerNotification.value = minesweeperGameWinnerNotifications.value[0]
  } else {
    checkForSpecialCollectibleWinner()
  }
}

function checkForSpecialCollectibleWinner() {
  if (specialCollectibleWinnerNotifications.value.length) {
    specialCollectibleWinnerNotification.value = specialCollectibleWinnerNotifications.value[0]
  } else {
    checkForLiveOpsChallengeWinner()
  }
}

async function checkForLiveOpsChallengeWinner() {
  if (liveOpsChallengeWinnerNotifications.value.length) {
    await fetchLeaderboards()
    liveOpsChallengeWinnerNotification.value = liveOpsChallengeWinnerNotifications.value[0]
  } else {
    checkForLimitedPack()
  }
}

function showNextLimitedPackOrProceed() {
  const next = limitedPackPopupQueue.value.shift()
  if (next) {
    setShowLimitedPackModal(true, next)
  } else {
    checkForNewBadge()
  }
}

function checkForLimitedPack() {
  if (showLimitedPackModal.value) return
  limitedPackPopupQueue.value = activeLimitedPackItems.value
    .filter(item => !!item.config.popupOrder)
    .sort((a, b) => a.config.popupOrder! - b.config.popupOrder!)
  showNextLimitedPackOrProceed()
}

function onCloseLiveOpsOverlay() {
  closeLiveOpsOverlay()
  checkForLiveOps()
}

function checkForLiveOps() {
  if (showLiveOpsOverlay.value) return
  const next = initialLiveOpsEvents.value.shift()
  if (next) {
    openLiveOpsOverlay(next.id, onCloseLiveOpsOverlay)
  }
}

function checkForMysteryBoxSale() {
  if (shouldRedirectToMysteryBoxSale.value) {
    markMysteryBoxSaleAsShown()
    router.push({ name: RouteName.MYSTERY_BOX, query: { fromInit: 'true' } })
  } else {
    checkForLiveOps()
  }
}

function onHideDailyUpWinnerModal() {
  dailyUpWinnerNotification.value = null
  checkForPathwayChallengeWinner()
}

function onHidePathwayChallengeWinnerModal() {
  pathwayChallengeWinnerNotification.value = null
  checkForPuzzlesCompletionChallengeWinner()
}

function onHidePuzzlesCompletionChallengeWinnerModal() {
  puzzlesCompletionChallengeWinnerNotification.value = null
  checkForPuzzleChallengeWinner()
}

function onHidePuzzleChallengeWinnerModal() {
  puzzleChallengeWinnerNotification.value = null
  checkForMemoryGameWinner()
}

function onHideMemoryGameWinnerModal() {
  memoryGameWinnerNotification.value = null
  checkForMinesweeperGameWinner()
}

function onHideMinesweeperGameWinnerModal() {
  minesweeperGameWinnerNotification.value = null
  checkForSpecialCollectibleWinner()
}

function onHideSpecialCollectibleWinnerModal() {
  specialCollectibleWinnerNotification.value = null
  checkForLiveOpsChallengeWinner()
}

function onHideLiveOpsChallengeWinnerModal() {
  liveOpsChallengeWinnerNotification.value = null
  checkForLimitedPack()
}

async function onHideLimitedPackModal() {
  setShowLimitedPackModal(false)
  await nextTick()
  showNextLimitedPackOrProceed()
}

function onHideChallengeModal() {
  showChallengeModal.value = false
  checkForDailyUpWinner()
}

function setBadgeFromNotification(badgeIndex: number) {
  const badgeNotification = badgeNotifications.value[badgeIndex]
  const badgeId = badgeNotification.badgeId
  currentBadgeId.value = badgeId
  const originalIndex = notifications.value.findIndex(n => isBadgeNotification(n) && n.badgeId === badgeId)
  if (originalIndex !== -1) {
    notifications.value.splice(originalIndex, 1)
  }
}

function checkForAchievementsBeforeRedirects() {
  checkForNewAchievementsOnInit()
  if (newAchievedMilestoneForOverlay.value) {
    pendingRedirectCheck.value = true
  } else {
    checkForMysteryBoxSale()
  }
}

function onAchievementOverlayHide() {
  newAchievedMilestoneForOverlay.value = null
  if (pendingRedirectCheck.value) {
    pendingRedirectCheck.value = false
    checkForMysteryBoxSale()
  }
}

function checkForHighlightedTasks() {
  //if (showTransactionModal.value) return
  showHighlightedTasksModal.value = hasNewHighlightedTasks.value
  if (!showHighlightedTasksModal.value) {
    checkForAchievementsBeforeRedirects()
  }
}

async function checkForV1Stars() {
  if (!isOnboardingDone.value) return
}

async function onWelcomeFromS1ModalHide() {
  showOnboardedFromS1Modal.value = false
  await checkForV1Stars()
}

function checkForNewBadge() {
  if (badgeNotifications.value.length) {
    getBadges()
    setBadgeFromNotification(0)
    setTimeout(() => {
      showBadgeOverlay.value = true
      currentNotificationIndex.value = 0
    }, 600)
  } else {
    checkForHighlightedTasks()
  }
}

function onBadgeOverlayClosed() {
  showBadgeOverlay.value = false
  checkForHighlightedTasks()
}

function closeOfflineProfitModal() {
  startCoinAnimation()
  setTimeout(() => {
    showOfflineProfitModal.value = false
    doAnimateCoins()
    setTimeout(() => {
      addOfflineProfitToCoins.value = true
    }, 250)
    setTimeout(() => {
      checkForDailyTxModals()
    }, 400)
  }, 150)
}

function checkForDailyTxModals() {
  if (hideAllInitModals.value) return
  if (!isOnboardingDone.value) return

  // if (canShowRadarTxModal.value) {
  //   setShowRadarTransactionModal(true)
  //   return
  // }
  //checkForDailyTonModal()
  checkForChallengeModal()
}

function checkForDailyTonModal() {
  if (canShowDailyTxModal.value) {
    setShowTransactionModal(true)
    return
  }
  checkForChallengeModal()
}

function closeDailyRadarTransactionModal() {
  setDailyRadarTxModalTimestamp()
  setShowRadarTransactionModal(false)
  setTimeout(() => {
    checkForDailyTonModal()
  }, 500)
}

function closeDailyTonTransactionModal() {
  setDailyTxModalTimestamp()
  setShowTransactionModal(false)
  isInitLoad.value = false
  setTimeout(() => {
    checkForChallengeModal()
  }, 500)
}

function hideUpgradesExtraSpinOverlay() {
  showUpgradesExtraSpinOverlay.value = false
  upgradesExtraSpinOverlayDisplayed.value = true
}

function closeHighlightModal() {
  showHighlightedTasksModal.value = false
  setNewTasksAsClosed()
  checkForAchievementsBeforeRedirects()
}

function closeBonusOverlay() {
  showBonusOverlay.value = false
  if (hasBonusWithTphX2.value && canShowBonusOverlay.value && (notificationReward.value.coins || notificationReward.value.spins)) {
    hasBonusWithTphX2.value = false
    setTimeout(() => {
      showBonusOverlay.value = true
    }, 500)
  } else {
    canShowBonusOverlay.value = false
    if (canShowOfflineProfitModal.value) {
      showOfflineProfitModal.value = true
    } else {
      checkForDailyTxModals()
    }
  }
}

function goToHighlightedTask(taskId: string) {
  closeHighlightModal()
  router.push({ name: RouteName.EARN, query: { openTaskId: taskId } })
}

watchOnce(offlineProfit, (offlineProfit, oldValue) => {
  if (oldValue !== undefined || offlineProfit < 1) {
    if (!player.value.onboarding?.done) return
    checkForDailyTxModals()
    return
  }
  if (hideAllInitModals.value) {
    addOfflineProfitToCoins.value = true
    return
  }
  canShowOfflineProfitModal.value = true
}, { immediate: true })

watch(allDailyDone, (allDailyDone) => {
  if (!allDailyDone) return
  if (router.currentRoute.value.fullPath !== routePath[RouteName.REWARDS]) {
    showAllDailyDoneOverlay.value = true
  }
})

useInit()

watch(playerLevel, () => {
  showLevelUpModal.value = true
  updateState()
})

watch(chosenToken, () => {
  setTimeout(() => {
    showTokenChangedModal.value = true
  }, 300)
})

watch(() => player.value.upgradeCount, () => {
  if (player.value.upgradeCount && !(player.value.upgradeCount % 10) && !upgradesExtraSpinOverlayDisplayed.value) {
    setTimeout(() => {
      showUpgradesExtraSpinOverlay.value = true
    }, 1000)
  } else {
    upgradesExtraSpinOverlayDisplayed.value = false
  }
})

watch(badgeNotifications, (newVal, oldVal) => {
  if (newVal.length > (oldVal?.length ?? 0) && !showBadgeOverlay.value) {
    checkForNewBadge()
  }
})

watch(() => [showBadgeOverlay.value, currentNotificationIndex.value], () => {
  if (!showBadgeOverlay.value && currentNotificationIndex.value !== undefined && notifications.value[currentNotificationIndex.value + 1]) {
    setTimeout(() => {
      if (currentNotificationIndex.value !== undefined) {
        setBadgeFromNotification(currentNotificationIndex.value + 1)
        showBadgeOverlay.value = true
        currentNotificationIndex.value += 1
      }
    }, 600)
  } else if (showBadgeOverlay.value) {
    setTimeout(() => {
      showConfetti()
    }, 600)
  }
  if (!showBadgeOverlay.value && currentNotificationIndex.value !== undefined && !notifications.value[currentNotificationIndex.value + 1]) {
    checkForHighlightedTasks()
  }
})

watch(canShowOfflineProfitModal, () => {
  if (hideAllInitModals.value) return
  if (!isOnboardingDone.value) return

  if (!showBonusOverlay.value && canShowBonusOverlay.value && notificationReward.value.tphX2) {
    hasBonusWithTphX2.value = true
    showBonusOverlay.value = true
  } else if (!showBonusOverlay.value && canShowBonusOverlay.value && (notificationReward.value.coins || notificationReward.value.spins)) {
    showBonusOverlay.value = true
  } else if (canShowOfflineProfitModal.value && !showBonusOverlay.value) {
    showOfflineProfitModal.value = true
  } else if (!showBonusOverlay.value) {
    checkForDailyTxModals()
  }
}, { immediate: true })

onBeforeMount(async () => {
  WebApp.setBackgroundColor('#000000')
  WebApp.setBottomBarColor('#000000')
  WebApp.disableVerticalSwipes()

  await router.push({ name: RouteName.REWARDS })
})

function checkForMiniGameReturn() {
  if (route.query.gameId) {
    if (route.query.gameId === MINI_GAME_ID.MEMORY_GAME) {
      openMemoryChallengeModal(true)
    } else if (route.query.gameId === MINI_GAME_ID.MINESWEEPER) {
      openMinesweeperChallengeModal(true)
    }
    removeQueryParams(['gameSubmitted', 'gameId'])
  }
}

onMounted(() => {
  checkForMiniGameReturn()
  // Check for V1 stars for returning users who already completed onboarding
  // Delay to allow other init modals to show first
  setTimeout(() => {
    if (!showOnboardedFromS1Modal.value) {
      checkForV1Stars()
    }
  }, 2000)
})
</script>

<template>
  <FlyingCoins :show="showFlyingCoins" />
  <BaseBackButton />
  <div
    id="confetti-container"
    class="fixed top-0 z-[9999] left-0 w-screen h-screen pointer-events-none"
    style="will-change: transform;"
  />

  <div
    class="flex-1 flex flex-col"
    :class="router.currentRoute.value.name === RouteName.HOME && 'h-full'"
    :style="fullScreenStyles"
  >
    <TheHeader v-if="!hideHeader" />

    <RouterView />

    <TheNavbar
      v-if="!hideNavbar"
      v-show="!isKeyboardOpen"
    />

    <BonusOverlay
      v-if="showBonusOverlay"
      :show="showBonusOverlay"
      :double-profit="hasBonusWithTphX2 ? notificationReward.tphX2 : 0"
      :coins="hasBonusWithTphX2 ? 0 : notificationReward.coins"
      :spins="hasBonusWithTphX2 || notificationReward.tickets ? 0 : notificationReward.spins"
      :tickets="hasBonusWithTphX2 ? 0 : notificationReward.tickets"
      @cta-click="closeBonusOverlay"
      @on-check-entries="router.push({name:RouteName.GIVEAWAY})"
    />

    <OfflineProfitModal
      v-if="showOfflineProfitModal"
      :show="showOfflineProfitModal"
      :profit="offlineProfit"
      :offline-profit-hours="offlineProfitHours"
      @hide="closeOfflineProfitModal"
    />

    <!--    <DailyTonTransactionModal-->
    <!--      v-if="showTransactionModal"-->
    <!--      :show="showTransactionModal"-->
    <!--      :is-done="claimedDailyTx"-->
    <!--      @hide="closeDailyTonTransactionModal"-->
    <!--    />-->

    <!--    <DailyRadarModal-->
    <!--      v-if="showRadarTransactionModal"-->
    <!--      :show="showRadarTransactionModal"-->
    <!--      :is-done="claimedDailyRadar"-->
    <!--      @hide="closeDailyRadarTransactionModal"-->
    <!--    />-->

    <LevelUpModal
      v-if="showLevelUpModal"
      :show="showLevelUpModal"
      :tap-config="playerTapConfig"
      :current-level="playerLevel"
      @hide="showLevelUpModal = false"
    />

    <AllDailyDoneOverlay
      v-if="showAllDailyDoneOverlay"
      :class="showAllDailyDoneOverlay ? 'z-[999] opacity-100' : '-z-[1] opacity-0'"
      :show="showAllDailyDoneOverlay"
      @close="showAllDailyDoneOverlay = false"
    />

    <ExtraSpinOverlay
      v-if="showUpgradesExtraSpinOverlay"
      :show="showUpgradesExtraSpinOverlay"
      :spin-count="upgradesExtraSpinDetails.count"
      :description="upgradesExtraSpinDetails.description"
      @close="hideUpgradesExtraSpinOverlay"
    />

    <TokenChangedModal
      v-if="showTokenChangedModal"
      :show="showTokenChangedModal"
      :token="chosenToken"
      @hide="showTokenChangedModal = false"
    />

    <TaskHighlightModal
      v-if="showHighlightedTasksModal"
      :show="showHighlightedTasksModal"
      :tasks="newHighlightedTasks"
      @open-task="goToHighlightedTask"
      @hide="closeHighlightModal"
    />

    <StarBalanceConfirmModal />

    <BadgeOverlay
      v-if="showBadgeOverlay"
      :show="showBadgeOverlay"
      :title="t('badge_overlay.title', 'Congrats, you won a badge!')"
      :name="badgeConfig?.name"
      :image="badgeConfig?.imageUrl"
      @click="onBadgeOverlayClosed"
    />

    <ChallengeModal
      v-if="showChallengeModal"
      :show="showChallengeModal"
      @hide="onHideChallengeModal"
    />

    <DailyUpChallengeModal
      v-if="showDailyUpChallengeModal"
      :show="showDailyUpChallengeModal"
      @hide="setShowDailyUpChallengeModal(false)"
    />

    <DailyUpChallengeWinnerModal
      v-if="dailyUpWinnerNotification"
      :show="!!dailyUpWinnerNotification"
      :notification="dailyUpWinnerNotification"
      @hide="onHideDailyUpWinnerModal"
    />

    <PathwayChallengeWinnerModal
      v-if="pathwayChallengeWinnerNotification"
      :show="!!pathwayChallengeWinnerNotification"
      :notification="pathwayChallengeWinnerNotification"
      @hide="onHidePathwayChallengeWinnerModal"
    />

    <PuzzleChallengeWinnerModal
      v-if="puzzleChallengeWinnerNotification"
      :show="!!puzzleChallengeWinnerNotification"
      :notification="puzzleChallengeWinnerNotification"
      @hide="onHidePuzzleChallengeWinnerModal"
    />

    <PuzzlesCompletionChallengeWinnerModal
      v-if="puzzlesCompletionChallengeWinnerNotification"
      :show="!!puzzlesCompletionChallengeWinnerNotification"
      :notification="puzzlesCompletionChallengeWinnerNotification"
      @hide="onHidePuzzlesCompletionChallengeWinnerModal"
    />

    <MemoryChallengeWinnerModal
      v-if="memoryGameWinnerNotification"
      :show="!!memoryGameWinnerNotification"
      :notification="memoryGameWinnerNotification"
      @hide="onHideMemoryGameWinnerModal"
    />

    <MinesweeperChallengeWinnerModal
      v-if="minesweeperGameWinnerNotification"
      :show="!!minesweeperGameWinnerNotification"
      :notification="minesweeperGameWinnerNotification"
      @hide="onHideMinesweeperGameWinnerModal"
    />

    <CollectibleChallengeWinnerModal
      v-if="specialCollectibleWinnerNotification"
      :show="!!specialCollectibleWinnerNotification"
      :notification="specialCollectibleWinnerNotification"
      @hide="onHideSpecialCollectibleWinnerModal"
    />

    <LiveOpsChallengeWinnerModal
      v-if="liveOpsChallengeWinnerNotification"
      :show="!!liveOpsChallengeWinnerNotification"
      :notification="liveOpsChallengeWinnerNotification"
      @hide="onHideLiveOpsChallengeWinnerModal"
    />

    <NumberFormattingModal
      v-if="showNumberFormatModal"
      :show="showNumberFormatModal"
      @hide="setShowNumberFormatModal(false)"
    />

    <AchievementOverlay
      v-if="showNewAchievedMilestoneOverlay"
      :show="showNewAchievedMilestoneOverlay"
      :achievement="newAchievedMilestoneForOverlay?.achievement"
      :category-title="newAchievedMilestoneForOverlay?.categoryTitle"
      :achievement-type="newAchievedMilestoneForOverlay?.achievementType"
      @hide="onAchievementOverlayHide"
    />

    <PathwayEventOverlay
      v-if="showPathwayEventOverlay && selectedPathwayEvent"
      :show="showPathwayEventOverlay"
      :pathway-event="selectedPathwayEvent"
      @hide="hidePathwayEventOverlay"
    />

    <ExchangeOverlay
      v-if="showExchangeOverlay && selectedExchange"
      :show="showExchangeOverlay"
      :exchange="selectedExchange"
      @hide="hideExchangeOverlay"
    />

    <PuzzlesOverlay
      v-if="showPuzzlesOverlay"
      :show="showPuzzlesOverlay"
      @hide="hidePuzzlesOverlay"
    />

    <PathwayChallengeModal
      v-if="showPathwayChallengeModal"
      :show="showPathwayChallengeModal"
      @hide="setShowPathwayChallengeModal(false)"
    />

    <PuzzleChallengeModal
      v-if="showPuzzleChallengeModal"
      :show="showPuzzleChallengeModal"
      @hide="setShowPuzzleChallengeModal(false)"
    />

    <PuzzlesCompletionChallengeModal
      v-if="showPuzzlesCompletionChallengeModal"
      :show="showPuzzlesCompletionChallengeModal"
      @hide="setShowPuzzlesCompletionChallengeModal(false)"
    />

    <MemoryChallengeOverlay
      v-if="showMemoryChallengeModal"
      :show="showMemoryChallengeModal"
      @hide="closeMemoryChallengeModal"
      @play="closeMemoryChallengeModal"
    />

    <MinesweeperChallengeOverlay
      v-if="showMinesweeperChallengeModal"
      :show="showMinesweeperChallengeModal"
      @hide="closeMinesweeperChallengeModal"
      @play="closeMinesweeperChallengeModal"
    />

    <CollectibleChallengeOverlay
      v-if="showCollectibleChallengeModal"
      :show="showCollectibleChallengeModal"
      @hide="closeCollectibleChallengeModal"
      @play="closeCollectibleChallengeModal"
    />

    <LiveOpsChallengeOverlay
      v-if="showLiveOpsChallengeModal"
      :show="showLiveOpsChallengeModal"
      @hide="closeLiveOpsChallengeModal"
    />

    <ViewGiftModal
      v-if="showGiftModal && selectedGift"
      :show="showGiftModal"
      :gift="selectedGift"
      modal-wrapper-class="z-[1000]"
      @hide="hideGiftModal"
    />

    <LimitedPackModal
      v-if="selectedLimitedPack"
      :limited-pack="selectedLimitedPack"
      :show="showLimitedPackModal"
      @hide="onHideLimitedPackModal"
    />

    <LiveOpsOverlay
      v-if="showLiveOpsOverlay && selectedLiveOpsEventId"
      :key="selectedLiveOpsEventId"
      :show="showLiveOpsOverlay"
      :event-id="selectedLiveOpsEventId"
      @hide="onCloseLiveOpsOverlay"
    />

    <PlantMineModal
      :show="showPlantMineModal"
      @hide="closePlantMineModal"
    />

    <MineTutorialModal
      :show="showMineTutorialModal"
      @hide="closeMineTutorialModal"
    />

    <UseMagnetOverlay
      :show="showUseMagnetOverlay"
      @hide="closeUseMagnetOverlay"
    />

    <MinesCollectedModal
      :show="showMinesCollectedModal"
      :acquired-count="collectedMinesCount"
      @hide="closeMinesCollectedModal"
    />
  </div>
  <Toaster class="fixed top-0 z-[999]" />
</template>

<style scoped>
.fade-enter-active {
	transition: opacity 0.6s ease-in;
	opacity: 1;
}

.fade-leave-active {
	transition: opacity 0.6s ease-out;
	opacity: 1;
}

.fade-enter-from {
	opacity: 0;
}

.fade-leave-to {
	opacity: 0;
}

.bg-fill-gradient {
	background: linear-gradient(180deg, #000 30%, rgba(0, 0, 0, 0.00) 100%);
}

</style>
